import * as React from 'react'
import { Editor, isNodeSelection, posToDOMRect } from '@tiptap/react'
import { clsx } from 'clsx'
import { BubbleMenuPlugin } from '../extensions/rich-text/extensions/bubble-menu/plugin'
import {
  useFloating,
  flip,
  shift,
  inline,
  autoUpdate,
  offset,
} from '@floating-ui/react'
import type { EditorView } from '@tiptap/pm/view'

export const CustomBubbleMenu = ({
  editor,
  children,
  setIsShowingBubbleMenu,
  blockId,
}: {
  editor: Editor
  children?: React.ReactNode
  isShowingBubbleMenu: boolean
  setIsShowingBubbleMenu: (isShowingBubbleMenu: boolean) => void
  blockId: string
}) => {
  const [isMouseDown, setIsMouseDown] = React.useState(false)
  const [shouldShowPrimitive, setShouldShowPrimitive] = React.useState(false)
  const [element, setElement] = React.useState<HTMLDivElement | null>(null)

  /**
   * Track mouse state
   */
  React.useEffect(() => {
    const dom = editor.view.dom
    if (!dom) return

    function handleMouseDown() {
      setIsMouseDown(true)
    }

    function handleMouseUp() {
      setIsMouseDown(false)
    }

    dom.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      dom.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [editor.view.dom])

  /**
   * If user is using the mouse, we don't want to show the bubble menu.
   * The popover will be instantiated, but we'll hide it with css.
   */
  const resolvedShouldShow = shouldShowPrimitive && !isMouseDown

  /**
   * Sync w external state
   */
  React.useEffect(() => {
    setIsShowingBubbleMenu(resolvedShouldShow)
  }, [resolvedShouldShow, setIsShowingBubbleMenu])

  const floating = useFloating({
    placement: 'top',
    open: true,
    onOpenChange: () => undefined,
    middleware: [
      inline(),
      flip(),
      shift({ padding: 12 }),
      offset({ mainAxis: 8 }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'absolute',
  })

  /**
   * Init plugin
   */
  React.useEffect(() => {
    if (!element) return
    if (editor.isDestroyed) return

    const pluginKey = 'bubbleMenu'
    const plugin = BubbleMenuPlugin({
      updateDelay: 0,
      editor,
      element,
      pluginKey,
      shouldShow: ({ editor, from, to, view }) => {
        let shouldShowPrimitive = false

        const selection = editor.state.selection
        const { empty } = selection
        if (empty || isNodeSelection(selection)) {
          shouldShowPrimitive = false
        } else {
          shouldShowPrimitive = true
        }

        setShouldShowPrimitive(shouldShowPrimitive)
        floating.refs.setReference({
          getBoundingClientRect() {
            return getClientRect(view, from, to)
          },
          getClientRects() {
            return [getClientRect(view, from, to)]
          },
        })
        return shouldShowPrimitive
      },
    })

    editor.registerPlugin(plugin)
    return () => {
      editor.unregisterPlugin(pluginKey)
    }
  }, [editor, element, floating.refs])

  return (
    <>
      <div
        ref={floating.refs.setFloating}
        style={{ ...floating.floatingStyles }}
        className={clsx(
          'z-tooltip relative',
          resolvedShouldShow &&
            (editor.isFocused ||
              element?.contains(document.activeElement) ||
              document.activeElement?.closest(
                `[data-rt-editor-popover-id="${blockId}"]`
              ))
            ? 'animate-fade-in duration-100'
            : '!invisible opacity-0 !select-none !pointer-events-none'
        )}
      >
        <div
          className="w-max group rounded-md border border-dark-border-solid h-fit p-1 bg-dark-base"
          ref={setElement}
        >
          {children}
        </div>
      </div>
    </>
  )
}

function getClientRect(view: EditorView, from: number, to: number) {
  if (isNodeSelection(view.state.selection)) {
    let node = view.nodeDOM(from) as HTMLElement

    const nodeViewWrapper = node.dataset.nodeViewWrapper
      ? node
      : node.querySelector('[data-node-view-wrapper]')

    if (nodeViewWrapper) {
      node = nodeViewWrapper.firstChild as HTMLElement
    }

    if (node) {
      return node.getBoundingClientRect()
    }
  }

  return posToDOMRect(view, from, to)
}
